import React from 'react'
import { useTranslation } from 'react-i18next'
import { useInsightContentData } from 'hooks/useInsightModalState'
import { InsightContentSettingType } from 'components/insightContent/types'
import UnexpectedErrorBoundary from 'components/UnexpectedErrorBoundary'
import InsightContentType from 'components/insightContent/enum/InsightContentType'
import SparklineOverview from 'components/insightContent/visualization/Overview/SparklineOverview'
import { getMeasureType } from 'utils/viewOptionUtil'
import ComparisonMode from 'enums/ComparisonMode'
import SideLabel from './SideLabel'
import RunSearchEffect from './RunSearchEffect'
import Title from './Title'
import formatMentionsDisplay from '../../utils'

const shouldShowSideLabel = (detailSetting: InsightContentSettingType) => {
  const {
    colComparisonMode,
    rowComparisonMode,
    pivotThemeInfos,
    tagNames,
    docNames
  } = detailSetting

  if (rowComparisonMode !== ComparisonMode.TOPICS) {
    return false
  }

  switch (colComparisonMode) {
    case ComparisonMode.THEMES:
      return pivotThemeInfos && pivotThemeInfos[0] === undefined

    case ComparisonMode.TAGS:
      return !tagNames

    case ComparisonMode.DOC_HIERARCHIES:
      return !docNames

    default:
      throw new Error(`Unknown view option: ${colComparisonMode}`)
  }
}

const SelectedItemMeasure = ({
  detailSetting,
  widgetId,
  isAudienceQuery
}: {
  detailSetting: InsightContentSettingType
  widgetId: number
  isAudienceQuery: boolean
}) => {
  const insightContent = useInsightContentData(InsightContentType.NAME_MEASURE_BY)
  const { t } = useTranslation()
  const { viewOption, displayTitleTooltip } = detailSetting

  const selectedValue =
    insightContent?.data?.[0]?.results?.[0]?.data?.metrics?.[0]?.dataset?.[0]?.set?.[0]

  const count = formatMentionsDisplay(selectedValue)

  return (
    <UnexpectedErrorBoundary>
      <RunSearchEffect detailSetting={detailSetting} isAudienceQuery={isAudienceQuery}>
        <SparklineOverview
          title={<Title viewOption={viewOption} />}
          displayTitleTooltip={displayTitleTooltip}
          titleTooltipContent={t('insightModalOverviewTooltip')}
          count={count}
          sparklineData={
            insightContent?.data?.[0]?.results?.[0]?.data?.metrics?.[1]?.dataset?.[0]?.set
          }
          measureType={getMeasureType(viewOption)[0]}
          status={insightContent?.status}
          customizedInfo={
            shouldShowSideLabel(detailSetting) && (
              <SideLabel
                detailSetting={detailSetting}
                widgetId={widgetId!}
                isAudienceQuery={isAudienceQuery!}
                selectedValue={selectedValue}
              />
            )
          }
        />
      </RunSearchEffect>
    </UnexpectedErrorBoundary>
  )
}

export default SelectedItemMeasure
